/**
 * What sign-up says when a portal's calculator handed a calculation over —
 * Citizen Web's sign-up, its phone screen that sends it to the app, and the
 * app's own screen that keeps it:
 *
 *   - the title names the portal as its language says it — "que fez na Ei!",
 *     "que fez na Tonnie" (the portal's `nameGender`);
 *   - the line under it says the answer the person read at the calculator's
 *     top, in the calculator's own words ("You can be away from Portugal for
 *     up to 183 days in one trip"); a limit already passed, as such; with no
 *     number to say, the trips.
 */
import { inoutDaysAnswerText } from './presentation';
import type { InOutHandoffSummary } from '@tonnie/types';
import type { TFunction } from 'i18next';

export function inoutHandoffTitle(portal: InOutHandoffSummary['portal'], t: TFunction): string {
  switch (portal.nameGender) {
    case 'feminine':
      return t('inout.signup.titleFeminine', { portal: portal.displayName });
    case 'masculine':
      return t('inout.signup.titleMasculine', { portal: portal.displayName });
    default: {
      const unreachable: never = portal.nameGender;
      throw new Error(`Unknown name gender: ${String(unreachable)}`);
    }
  }
}

export function inoutHandoffDescription(
  summary: InOutHandoffSummary['summary'],
  {
    t,
    formatNumber,
    countryName,
  }: {
    t: TFunction;
    formatNumber: (value: number) => string;
    countryName: (code: string) => string;
  }
): string {
  const { answer } = summary;
  switch (answer.kind) {
    case 'oneTrip':
    case 'presenceNeeded':
      return t('inout.signup.descriptionAnswer', {
        answer: inoutDaysAnswerText(answer, {
          t,
          formatNumber,
          country: countryName(summary.countryCode),
        }),
      });
    case 'beyond':
      return t('inout.signup.descriptionBeyond');
    case 'none':
      return t('inout.signup.description', { count: summary.trips });
    default: {
      const unreachable: never = answer;
      throw new Error(`Unknown calculation answer: ${String(unreachable)}`);
    }
  }
}
